import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Search,
  X,
  Compass,
  Sun,
  Moon,
  HelpCircle,
  Info,
  TrendingUp,
  Layers,
  Zap,
  Activity,
  Grid,
  FileText,
  DollarSign,
  Bookmark,
  Eye,
  LineChart,
  History,
  Star,
  Landmark,
  FolderTree,
} from 'lucide-react';
import { fetchAllStockData } from '../../api';
import { useWatchlist } from '../../context/WatchlistContext';
import { useStockDrawer } from '../../context/StockDrawerContext';
import { useTheme } from '../../context/ThemeContext';
import { useAppTour } from '../../context/AppTourContext';
import { useFnoSymbols } from '../../hooks/useFnoSymbols';
import { openPageInfoModal } from './PageInfoModal';
import { openMutualFundModal } from '../modals/MutualFundFlowModal';
import { openFiiDiiModal } from '../modals/FiiDiiExplorerModal';
import CompanyLogo from './CompanyLogo';
import StockSymbolLabel from './StockSymbolLabel';
import { KiteIcon } from './KiteIcon';
import companyNamesData from '../../constants/companyNames.json';

const StockChartModal = React.lazy(() => import('./StockChartModal'));
const KiteTradeModal = React.lazy(() =>
  import('../modals/KiteTradeModal').then((m) => ({ default: m.KiteTradeModal }))
);

const companyNamesMap: Record<string, string> = companyNamesData as Record<string, string>;

export type CommandCategory = 'all' | 'stocks' | 'desks' | 'actions';

interface NavigationDesk {
  id: string;
  title: string;
  subtitle: string;
  path: string;
  group: string;
  keywords: string[];
  icon: React.ReactNode;
}

const NAVIGATION_DESKS: NavigationDesk[] = [
  {
    id: 'dashboard',
    title: 'Market Dashboard',
    subtitle: 'Broad-market breadth, market stance regime & top sectors',
    path: '/',
    group: 'Overview',
    keywords: ['stance', 'regime', 'nifty', 'advance', 'decline', 'cockpit', 'home'],
    icon: <Activity size={16} />,
  },
  {
    id: 'ladder',
    title: 'Accumulation Ladder',
    subtitle: 'Multi-day institutional persistence tiers (1D to 15D+ streaks)',
    path: '/overview',
    group: 'Market Internals & Breadth',
    keywords: ['streak', 'accumulation', 'ladder', 'golden stack', '21 ema', 'persistence', 'institutional'],
    icon: <Layers size={16} />,
  },
  {
    id: 'market-breadth',
    title: 'Market Breadth Desk',
    subtitle: '21/50/200 EMA breadth speedometer, thrust radar & heat grid',
    path: '/market-breadth',
    group: 'Market Internals & Breadth',
    keywords: ['breadth', 'speedometer', 'thrust', 'radar', 'ema', 'participation', 'internals'],
    icon: <TrendingUp size={16} />,
  },
  {
    id: 'opportunities',
    title: 'Breakout & Opportunities Scanner',
    subtitle: 'Pivot breakouts, 52-week highs, and approaching setups',
    path: '/opportunities',
    group: 'Setups & Scanners',
    keywords: ['breakout', 'opportunities', 'pivot', '52w', 'approaching', 'setups', 'scanner'],
    icon: <Zap size={16} />,
  },
  {
    id: 'volumeshockers',
    title: 'Volume Shockers',
    subtitle: 'Abnormal volume surge spikes vs 30-day average',
    path: '/volumeshockers',
    group: 'Setups & Scanners',
    keywords: ['volume', 'shockers', 'surge', 'burst', 'abnormal', 'spikes'],
    icon: <Activity size={16} />,
  },
  {
    id: 'index-heatmap',
    title: 'Indices Heatmap',
    subtitle: 'Interactive treemap of sectoral & benchmark performance',
    path: '/index-heatmap',
    group: 'Market Internals & Breadth',
    keywords: ['heatmap', 'indices', 'nifty 50', 'sectors', 'treemap'],
    icon: <Grid size={16} />,
  },
  {
    id: 'corporate-feeds',
    title: 'Corporate Feeds & Catalysts',
    subtitle: 'NSE exchange filings, board meetings, and AI analysis',
    path: '/corporate-feeds',
    group: 'Research',
    keywords: ['filings', 'announcements', 'board meeting', 'catalyst', 'results', 'news', 'pdf'],
    icon: <FileText size={16} />,
  },
  {
    id: 'bulk-block-deals',
    title: 'Bulk & Block Deals',
    subtitle: 'Institutional large transactions and marquee investor tracker',
    path: '/bulk-block-deals',
    group: 'Research',
    keywords: ['deals', 'bulk', 'block', 'institutional', 'fii', 'dii', 'clients'],
    icon: <DollarSign size={16} />,
  },
  {
    id: 'watchlist',
    title: 'Watchlist & Focus Radar',
    subtitle: 'Your curated stock portfolio and tracking lists',
    path: '/watchlist',
    group: 'Research',
    keywords: ['watchlist', 'saved', 'portfolio', 'radar', 'focus'],
    icon: <Bookmark size={16} />,
  },
  {
    id: 'sector-explorer',
    title: 'Sector Explorer & Drilldown',
    subtitle: 'Institutional sector drilldown cockpit, benchmark charts & constituents',
    path: '/sector',
    group: 'Research',
    keywords: ['sector', 'drilldown', 'sector explorer', 'nifty it', 'nifty bank', 'constituents', 'industry'],
    icon: <FolderTree size={16} />,
  },
  {
    id: 'industry',
    title: 'Industry & Sector Analysis',
    subtitle: 'Deep dive into 100+ sub-sectors & capital rotation',
    path: '/industry',
    group: 'Market Internals & Breadth',
    keywords: ['industry', 'subsector', 'rotation', 'breakdown', 'sectors'],
    icon: <Layers size={16} />,
  },
  {
    id: 'overboughtsold',
    title: 'Overbought & Oversold Scanner',
    subtitle: 'Extreme RSI stretch and mean-reversion candidates',
    path: '/overboughtsold',
    group: 'Setups & Scanners',
    keywords: ['overbought', 'oversold', 'rsi', 'stretch', 'mean reversion'],
    icon: <Activity size={16} />,
  },
  {
    id: 'guide',
    title: 'Trading Playbook & User Guide',
    subtitle: 'How to use NiftySutra to spot Dalal Street leaders',
    path: '/guide',
    group: 'Support',
    keywords: ['playbook', 'guide', 'tutorial', 'help', 'docs', 'rules'],
    icon: <HelpCircle size={16} />,
  },
];

const RECENT_STORAGE_KEY = 'niftysutra_cmd_recent_searches';

interface RecentItem {
  id: string;
  type: 'stock' | 'desk';
  symbol?: string;
  title: string;
  path?: string;
}

let cachedUniverse: string[] | null = null;
let universePromise: Promise<string[]> | null = null;

const loadUniverse = async (): Promise<string[]> => {
  if (cachedUniverse) return cachedUniverse;
  if (!universePromise) {
    try {
      const call = fetchAllStockData();
      if (call && typeof call.then === 'function') {
        universePromise = call
          .then((symbols) => {
            if (Array.isArray(symbols)) {
              cachedUniverse = symbols.map((s) => String(s).trim().toUpperCase()).filter(Boolean);
            } else {
              cachedUniverse = [];
            }
            return cachedUniverse;
          })
          .catch(() => {
            return [];
          })
          .finally(() => {
            universePromise = null;
          });
      } else {
        return Promise.resolve([]);
      }
    } catch {
      return Promise.resolve([]);
    }
  }
  return universePromise;
};

export const GlobalCommandPalette: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isInWatchlist } = useWatchlist();
  const { openStockDrawer } = useStockDrawer();
  const { isDark, toggleTheme } = useTheme();
  const { startTour } = useAppTour();
  const { isFnoSymbol } = useFnoSymbols();

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeTab, setActiveTab] = useState<CommandCategory>('all');
  const [stockUniverse, setStockUniverse] = useState<string[]>(cachedUniverse ?? []);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Sub-modal triggers from inside palette
  const [kiteSymbol, setKiteSymbol] = useState<string | null>(null);
  const [chartSymbol, setChartSymbol] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement>(null);
  const resultsContainerRef = useRef<HTMLDivElement>(null);

  // Platform detection for ⌘K vs Ctrl+K
  const isMac = useMemo(() => {
    return typeof navigator !== 'undefined' && /Mac|iPod|iPhone|iPad/.test(navigator.userAgent);
  }, []);

  // Recent items state
  const [recentItems, setRecentItems] = useState<RecentItem[]>(() => {
    try {
      const stored = localStorage.getItem(RECENT_STORAGE_KEY);
      if (stored) return JSON.parse(stored);
    } catch {}
    return [
      { id: 'ladder', type: 'desk', title: 'Accumulation Ladder', path: '/overview' },
      { id: 'market-breadth', type: 'desk', title: 'Market Breadth Desk', path: '/market-breadth' },
      { id: 'opportunities', type: 'desk', title: 'Breakout Scanner', path: '/opportunities' },
    ];
  });

  const saveRecentItem = useCallback((item: RecentItem) => {
    setRecentItems((prev) => {
      const filtered = prev.filter((r) => r.id !== item.id);
      const next = [item, ...filtered].slice(0, 5);
      try {
        localStorage.setItem(RECENT_STORAGE_KEY, JSON.stringify(next));
      } catch {}
      return next;
    });
  }, []);

  // Global Keyboard listener: Cmd+K / Ctrl+K and /
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const isTyping = target
        ? ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) || target.isContentEditable
        : false;

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setIsOpen((prev) => !prev);
        return;
      }

      if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        setIsOpen(false);
        return;
      }

      if (e.key === '/' && !isTyping && !isOpen) {
        e.preventDefault();
        setIsOpen(true);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Load stock universe when opened
  useEffect(() => {
    if (isOpen) {
      setSelectedIndex(0);
      if (!cachedUniverse) {
        loadUniverse()
          ?.then((symbols) => {
            if (Array.isArray(symbols)) {
              setStockUniverse(symbols);
            }
          })
          .catch(() => {});
      } else {
        setStockUniverse(cachedUniverse);
      }
    } else {
      setQuery('');
      setActiveTab('all');
    }
  }, [isOpen]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 40);
    }
  }, [isOpen]);

  // Reset selected index when query or tab changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query, activeTab]);

  // Filtered Stock Results
  const filteredStocks = useMemo(() => {
    if (activeTab === 'desks' || activeTab === 'actions') return [];
    const cleanQ = query.trim().toUpperCase();
    if (!cleanQ) {
      return [];
    }

    const matches: { symbol: string; score: number; companyName: string }[] = [];

    for (const sym of stockUniverse) {
      const cName = (companyNamesMap[sym] || '').toUpperCase();
      let score = -1;

      if (sym === cleanQ) {
        score = 0;
      } else if (sym.startsWith(cleanQ)) {
        score = 1;
      } else if (cName.startsWith(cleanQ)) {
        score = 2;
      } else if (sym.includes(cleanQ)) {
        score = 3;
      } else if (cName.includes(cleanQ)) {
        score = 4;
      }

      if (score >= 0) {
        // Boost if saved in watchlist
        const finalScore = isInWatchlist(sym) ? score - 0.5 : score;
        matches.push({ symbol: sym, score: finalScore, companyName: companyNamesMap[sym] || '' });
      }
    }

    matches.sort((a, b) => a.score - b.score);
    return matches.slice(0, 8);
  }, [query, stockUniverse, activeTab, isInWatchlist]);

  // Filtered Navigation Desks
  const filteredDesks = useMemo(() => {
    if (activeTab === 'stocks' || activeTab === 'actions') return [];
    const cleanQ = query.trim().toLowerCase();
    if (!cleanQ) {
      return NAVIGATION_DESKS.slice(0, 6);
    }

    const matches = NAVIGATION_DESKS.filter((desk) => {
      return (
        desk.title.toLowerCase().includes(cleanQ) ||
        desk.subtitle.toLowerCase().includes(cleanQ) ||
        desk.group.toLowerCase().includes(cleanQ) ||
        desk.keywords.some((k) => k.includes(cleanQ))
      );
    });

    return matches.sort((a, b) => {
      const aTitle = a.title.toLowerCase().includes(cleanQ);
      const bTitle = b.title.toLowerCase().includes(cleanQ);
      if (aTitle && !bTitle) return -1;
      if (!aTitle && bTitle) return 1;
      return 0;
    });
  }, [query, activeTab]);

  // Filtered Quick Actions
  const filteredActions = useMemo(() => {
    if (activeTab === 'stocks' || activeTab === 'desks') return [];
    const cleanQ = query.trim().toLowerCase();

    const actions = [
      {
        id: 'toggle-theme',
        title: isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode',
        desc: 'Toggle application color scheme',
        icon: isDark ? <Sun size={16} /> : <Moon size={16} />,
        action: () => toggleTheme(),
      },
      {
        id: 'start-tour',
        title: 'Start Guided App Tour',
        desc: 'Interactive walkthrough of Dalal Street tools',
        icon: <Compass size={16} />,
        action: () => startTour(),
      },
      {
        id: 'page-info',
        title: 'About Current Desk',
        desc: 'Understand metrics and playbook on this page',
        icon: <Info size={16} />,
        action: () => openPageInfoModal(location.pathname),
      },
      {
        id: 'mf-desk',
        title: 'Mutual Fund Flows & 5-Year SIP Trajectory',
        desc: 'AMFI Monthly Trends (2020–2026), SIP Inflows & SEBI Pure Equity activity',
        icon: <TrendingUp size={16} />,
        action: () => openMutualFundModal(),
      },
      {
        id: 'fiidii-desk',
        title: 'Institutional Cash Market Activity (FII & DII)',
        desc: '14-Year Macro Matrix (2012–2026), rolling inflows & institutional pulse',
        icon: <Landmark size={16} />,
        action: () => openFiiDiiModal(),
      },
    ];

    if (!cleanQ) return actions;
    return actions.filter(
      (a) => a.title.toLowerCase().includes(cleanQ) || a.desc.toLowerCase().includes(cleanQ)
    );
  }, [query, activeTab, isDark, toggleTheme, startTour, location.pathname]);

  // Flattened items for unified arrow key navigation
  type FlattenedItem =
    | { type: 'stock'; symbol: string; companyName: string }
    | { type: 'desk'; desk: NavigationDesk }
    | { type: 'action'; id: string; title: string; desc: string; icon: React.ReactNode; run: () => void }
    | { type: 'recent'; recent: RecentItem };

  const allItems: FlattenedItem[] = useMemo(() => {
    const list: FlattenedItem[] = [];

    // If query is empty and on 'all' tab, show recent searches first
    if (!query.trim() && activeTab === 'all' && recentItems.length > 0) {
      recentItems.forEach((r) => list.push({ type: 'recent', recent: r }));
    }

    filteredStocks.forEach((s) =>
      list.push({ type: 'stock', symbol: s.symbol, companyName: s.companyName })
    );

    filteredDesks.forEach((d) => list.push({ type: 'desk', desk: d }));

    filteredActions.forEach((a) =>
      list.push({ type: 'action', id: a.id, title: a.title, desc: a.desc, icon: a.icon, run: a.action })
    );

    return list;
  }, [query, activeTab, recentItems, filteredStocks, filteredDesks, filteredActions]);

  // Keep selected index within bounds
  useEffect(() => {
    setSelectedIndex((prev) => {
      if (allItems.length === 0) return 0;
      return Math.min(prev, allItems.length - 1);
    });
  }, [allItems.length]);

  const handleExecuteItem = useCallback(
    (item: FlattenedItem) => {
      if (!item) return;

      if (item.type === 'stock') {
        saveRecentItem({
          id: `stock_${item.symbol}`,
          type: 'stock',
          symbol: item.symbol,
          title: item.symbol,
        });
        setIsOpen(false);
        navigate(`/history/${encodeURIComponent(item.symbol)}`);
      } else if (item.type === 'desk') {
        saveRecentItem({
          id: item.desk.id,
          type: 'desk',
          title: item.desk.title,
          path: item.desk.path,
        });
        setIsOpen(false);
        navigate(item.desk.path);
      } else if (item.type === 'action') {
        setIsOpen(false);
        item.run();
      } else if (item.type === 'recent') {
        setIsOpen(false);
        if (item.recent.type === 'stock' && item.recent.symbol) {
          navigate(`/history/${encodeURIComponent(item.recent.symbol)}`);
        } else if (item.recent.path) {
          navigate(item.recent.path);
        }
      }
    },
    [navigate, saveRecentItem]
  );

  // Keyboard navigation inside palette
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev + 1) % Math.max(1, allItems.length));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev - 1 + allItems.length) % Math.max(1, allItems.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (allItems[selectedIndex]) {
        handleExecuteItem(allItems[selectedIndex]);
      }
    } else if (e.key === 'Tab') {
      e.preventDefault();
      const tabs: CommandCategory[] = ['all', 'stocks', 'desks', 'actions'];
      const nextIdx = (tabs.indexOf(activeTab) + 1) % tabs.length;
      setActiveTab(tabs[nextIdx]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
    }
  };

  // Scroll active item into view
  useEffect(() => {
    if (!resultsContainerRef.current) return;
    const selectedEl = resultsContainerRef.current.querySelector('.ns-cmd-item.is-selected');
    if (selectedEl) {
      selectedEl.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  return (
    <>
      {/* TopBar Interactive Search Pill Trigger */}
      <button
        type="button"
        className="ns-command-palette-trigger"
        onClick={() => setIsOpen(true)}
        title={`Search stocks, desks & tools (${isMac ? '⌘K' : 'Ctrl+K'})`}
        aria-label="Open command palette"
      >
        <Search size={15} className="ns-command-palette-trigger__icon" />
        <span className="ns-command-palette-trigger__text">
          Search stocks...
        </span>
        <kbd className="ns-command-palette-trigger__kbd">
          {isMac ? '⌘K' : 'Ctrl+K'}
        </kbd>
      </button>

      {/* Modal Dialog (Portaled to document.body) */}
      {isOpen && typeof document !== 'undefined'
        ? createPortal(
            <div
              className="ns-cmd-overlay"
              onClick={(e) => {
                if (e.target === e.currentTarget) setIsOpen(false);
              }}
              role="dialog"
              aria-modal="true"
              aria-label="Command Palette"
            >
              <div
                className="ns-cmd-dialog"
                onClick={(e) => e.stopPropagation()}
                onKeyDown={handleKeyDown}
              >
                {/* Search Bar Header */}
                <div className="ns-cmd-header">
                  <Search size={18} className="ns-cmd-search-icon" aria-hidden="true" />
                  <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setSelectedIndex(0);
                    }}
                    placeholder="Search stocks by symbol/name, desks or actions..."
                    className="ns-cmd-input"
                    aria-autocomplete="list"
                  />
                  {query && (
                    <button
                      type="button"
                      className="ns-cmd-close-btn"
                      onClick={() => setQuery('')}
                      aria-label="Clear query"
                    >
                      <X size={15} />
                    </button>
                  )}
                  <button
                    type="button"
                    className="ns-cmd-close-btn"
                    onClick={() => setIsOpen(false)}
                    aria-label="Close command palette (Esc)"
                  >
                    <X size={17} />
                  </button>
                </div>

                {/* Category Filter Tabs */}
                <div className="ns-cmd-tabs">
                  <button
                    type="button"
                    className={`ns-cmd-tab ${activeTab === 'all' ? 'is-active' : ''}`}
                    onClick={() => setActiveTab('all')}
                  >
                    <span>All</span>
                    <span className="ns-cmd-tab__count">{allItems.length}</span>
                  </button>
                  <button
                    type="button"
                    className={`ns-cmd-tab ${activeTab === 'stocks' ? 'is-active' : ''}`}
                    onClick={() => setActiveTab('stocks')}
                  >
                    <span>Stocks</span>
                    {filteredStocks.length > 0 && (
                      <span className="ns-cmd-tab__count">{filteredStocks.length}</span>
                    )}
                  </button>
                  <button
                    type="button"
                    className={`ns-cmd-tab ${activeTab === 'desks' ? 'is-active' : ''}`}
                    onClick={() => setActiveTab('desks')}
                  >
                    <span>Desks</span>
                    <span className="ns-cmd-tab__count">{filteredDesks.length}</span>
                  </button>
                  <button
                    type="button"
                    className={`ns-cmd-tab ${activeTab === 'actions' ? 'is-active' : ''}`}
                    onClick={() => setActiveTab('actions')}
                  >
                    <span>Actions</span>
                    <span className="ns-cmd-tab__count">{filteredActions.length}</span>
                  </button>
                </div>

                {/* Results List */}
                <div className="ns-cmd-list" ref={resultsContainerRef} role="listbox">
                  {allItems.length === 0 ? (
                    <div className="ns-cmd-empty">
                      <Search size={28} style={{ opacity: 0.3 }} />
                      <strong className="ns-cmd-empty__title">No matching results</strong>
                      <span className="ns-cmd-empty__desc">
                        Try searching with an NSE symbol, company name, or desk keyword.
                      </span>
                    </div>
                  ) : (
                    allItems.map((item, idx) => {
                      const isSelected = idx === selectedIndex;

                      if (item.type === 'recent') {
                        return (
                          <div
                            key={`recent_${item.recent.id}`}
                            className={`ns-cmd-item ${isSelected ? 'is-selected' : ''}`}
                            onClick={() => handleExecuteItem(item)}
                            onMouseEnter={() => setSelectedIndex(idx)}
                            role="option"
                            aria-selected={isSelected}
                          >
                            <div className="ns-cmd-item__left">
                              <div className="ns-cmd-item__icon-box">
                                <History size={16} />
                              </div>
                              <div className="ns-cmd-item__content">
                                <div className="ns-cmd-item__title-row">
                                  <span className="ns-cmd-item__title">{item.recent.title}</span>
                                  <span className="ns-cmd-item__badge">Recent</span>
                                </div>
                                <span className="ns-cmd-item__desc">
                                  {item.recent.type === 'stock' ? 'Stock Explorer' : item.recent.path}
                                </span>
                              </div>
                            </div>
                            <span className="ns-cmd-item__hint">Jump ↵</span>
                          </div>
                        );
                      }

                      if (item.type === 'stock') {
                        const inWatchlist = isInWatchlist(item.symbol);
                        const isFno = isFnoSymbol(item.symbol);

                        return (
                          <div
                            key={`stock_${item.symbol}`}
                            className={`ns-cmd-item ${isSelected ? 'is-selected' : ''}`}
                            onClick={() => handleExecuteItem(item)}
                            onMouseEnter={() => setSelectedIndex(idx)}
                            role="option"
                            aria-selected={isSelected}
                          >
                            <div className="ns-cmd-item__left">
                              <CompanyLogo symbol={item.symbol} name={item.companyName} size={28} shape="circle" />
                              <div className="ns-cmd-item__content">
                                <div className="ns-cmd-item__title-row">
                                  <StockSymbolLabel symbol={item.symbol} />
                                  {isFno && (
                                    <span className="ns-cmd-item__badge ns-cmd-item__badge--fno">F&amp;O</span>
                                  )}
                                  {inWatchlist && (
                                    <span className="ns-cmd-item__badge ns-cmd-item__badge--watchlist">
                                      <Star size={9} fill="currentColor" /> Watchlist
                                    </span>
                                  )}
                                </div>
                                {item.companyName && (
                                  <span className="ns-cmd-item__desc">{item.companyName}</span>
                                )}
                              </div>
                            </div>

                            {/* Stock Quick Action Buttons */}
                            <div className="ns-cmd-item__actions">
                              <button
                                type="button"
                                className="ns-cmd-action-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setIsOpen(false);
                                  openStockDrawer(item.symbol);
                                }}
                                title="Open Quick Peek Drawer"
                                aria-label="Open Quick Peek"
                              >
                                <Eye size={13} />
                              </button>

                              <button
                                type="button"
                                className="ns-cmd-action-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setIsOpen(false);
                                  setChartSymbol(item.symbol);
                                }}
                                title="Open Candlestick Chart"
                                aria-label="Open Chart"
                              >
                                <LineChart size={13} />
                              </button>

                              <button
                                type="button"
                                className="ns-cmd-action-btn ns-cmd-action-btn--kite"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setIsOpen(false);
                                  setKiteSymbol(item.symbol);
                                }}
                                title="Trade on Zerodha Kite"
                                aria-label="Trade on Kite"
                              >
                                <KiteIcon size={12} />
                              </button>

                              <span className="ns-cmd-item__hint">↵</span>
                            </div>
                          </div>
                        );
                      }

                      if (item.type === 'desk') {
                        return (
                          <div
                            key={`desk_${item.desk.id}`}
                            className={`ns-cmd-item ${isSelected ? 'is-selected' : ''}`}
                            onClick={() => handleExecuteItem(item)}
                            onMouseEnter={() => setSelectedIndex(idx)}
                            role="option"
                            aria-selected={isSelected}
                          >
                            <div className="ns-cmd-item__left">
                              <div className="ns-cmd-item__icon-box">{item.desk.icon}</div>
                              <div className="ns-cmd-item__content">
                                <div className="ns-cmd-item__title-row">
                                  <span className="ns-cmd-item__title">{item.desk.title}</span>
                                  <span className="ns-cmd-item__badge">{item.desk.group}</span>
                                </div>
                                <span className="ns-cmd-item__desc">{item.desk.subtitle}</span>
                              </div>
                            </div>
                            <span className="ns-cmd-item__hint">Jump ↵</span>
                          </div>
                        );
                      }

                      if (item.type === 'action') {
                        return (
                          <div
                            key={`action_${item.id}`}
                            className={`ns-cmd-item ${isSelected ? 'is-selected' : ''}`}
                            onClick={() => handleExecuteItem(item)}
                            onMouseEnter={() => setSelectedIndex(idx)}
                            role="option"
                            aria-selected={isSelected}
                          >
                            <div className="ns-cmd-item__left">
                              <div className="ns-cmd-item__icon-box">{item.icon}</div>
                              <div className="ns-cmd-item__content">
                                <span className="ns-cmd-item__title">{item.title}</span>
                                <span className="ns-cmd-item__desc">{item.desc}</span>
                              </div>
                            </div>
                            <span className="ns-cmd-item__hint">Run ↵</span>
                          </div>
                        );
                      }

                      return null;
                    })
                  )}
                </div>

                {/* Footer Keyboard Hints */}
                <div className="ns-cmd-footer">
                  <div className="ns-cmd-footer__shortcuts">
                    <span>
                      <kbd className="ns-cmd-kbd">↑</kbd> <kbd className="ns-cmd-kbd">↓</kbd> to navigate
                    </span>
                    <span>
                      <kbd className="ns-cmd-kbd">↵</kbd> to select
                    </span>
                    <span>
                      <kbd className="ns-cmd-kbd">Tab</kbd> to filter
                    </span>
                    <span>
                      <kbd className="ns-cmd-kbd">Esc</kbd> to close
                    </span>
                  </div>
                  <div>
                    <span>NiftySutra Command Spotlight</span>
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}

      <React.Suspense fallback={null}>
        {/* Kite Trade Modal (if opened via quick action) */}
        {kiteSymbol && (
          <KiteTradeModal
            isOpen={Boolean(kiteSymbol)}
            onClose={() => setKiteSymbol(null)}
            symbol={kiteSymbol}
            companyName={companyNamesMap[kiteSymbol] || ''}
          />
        )}

        {/* Candlestick Chart Modal (if opened via quick action) */}
        {chartSymbol && (
          <StockChartModal
            symbol={chartSymbol}
            onClose={() => setChartSymbol(null)}
            showBreakoutLine={false}
          />
        )}
      </React.Suspense>
    </>
  );
};

export default React.memo(GlobalCommandPalette);
